import { useEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../lib/useClickOutside';
import { Button } from '../../components/ui/button';

type Props = {
  value: string | null;
  locationLabel: string;           // "Perth City" (or full account name fall-back)
  archived: boolean;               // rollout-level archived flag
  onSave: (next: string | null) => Promise<void>;
};

// Notes cell on the rollout master grid. Inline preview is truncated to the
// first line; click opens a popover-anchored textarea for real editing.
//
// Dismiss rules (spec §7.1 polish):
//   - Save commits via onSave (existing row-patch pipeline)
//   - Cancel / click-outside / Escape discards the edit
//   - Archived rollouts: popover opens in read-only mode (no Save button)
//
// The popover positions itself below the cell; overflow is handled by the
// browser (fixed-position, anchored via the cell's bounding rect). Using
// fixed positioning avoids the grid's overflow-x: auto clipping the popover.

export function NotesCell({ value, locationLabel, archived, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string>(value ?? '');
  const [submitting, setSubmitting] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Click-outside + Escape close the popover AND discard the draft. The
  // anchor stays in the ref list so clicking the preview itself doesn't
  // toggle-close-then-open in a single pointerdown.
  useClickOutside(
    [anchorRef, popoverRef],
    () => {
      setOpen(false);
      setDraft(value ?? '');
    },
    open,
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setDraft(value ?? '');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, value]);

  // When the row value changes (e.g. another tab wrote to the row, or a refetch
  // arrived), refresh the draft so reopening doesn't show stale text. Skipped
  // while the popover is open so in-progress edits aren't clobbered.
  useEffect(() => {
    if (!open) setDraft(value ?? '');
  }, [value, open]);

  // Position the popover next to the anchor using fixed coords. Measured on
  // open (and on viewport resize while open). Without fixed positioning the
  // table's overflow-x container clips the popover.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    const compute = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const popWidth = 420;
      // Right-align the popover if anchoring-left would overflow the viewport.
      const left = Math.min(rect.left, window.innerWidth - popWidth - 16);
      const top = rect.bottom + 4;
      setPos({ top, left: Math.max(16, left) });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open]);

  const commit = async () => {
    if (archived || submitting) return;
    const next = draft.trim() === '' ? null : draft;
    if (next === value) {
      // No-op: close without a write.
      setOpen(false);
      return;
    }
    setSubmitting(true);
    try {
      await onSave(next);
      setOpen(false);
    } catch {
      // Mutation error surfaces via the existing react-query error path on
      // the MasterGrid mutation; keep the popover open so the user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  const hasValue = typeof value === 'string' && value.length > 0;
  const previewText = hasValue ? firstLine(value) : 'Add note…';

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={hasValue ? `Edit notes for ${locationLabel}` : `Add notes for ${locationLabel}`}
        title={hasValue ? value : 'Add note'}
        className={`w-44 text-left text-xs rounded border border-slate-200 px-2 py-1 truncate ${
          hasValue ? 'text-slate-700 hover:bg-slate-50' : 'text-slate-400 italic hover:bg-slate-50'
        }`}
      >
        {previewText}
      </button>

      {open && pos && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={`Row notes for ${locationLabel}`}
          className="fixed z-40 w-[420px] rounded-md border border-slate-300 bg-white shadow-xl p-3"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100">
            <span className="text-xs uppercase tracking-wide font-medium text-slate-600">
              Row notes for {locationLabel}
            </span>
            {archived && (
              <span className="text-[10px] uppercase tracking-wide text-slate-400">
                read-only (archived)
              </span>
            )}
          </div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            readOnly={archived}
            rows={7}
            placeholder={archived ? '' : 'Any notes for the media buyers, the client, or next you…'}
            className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 resize-y"
            autoFocus={!archived}
          />
          <div className="mt-3 flex items-center justify-end gap-2">
            {archived ? (
              <Button variant="outline" onClick={() => setOpen(false)}>
                Close
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setOpen(false);
                    setDraft(value ?? '');
                  }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button onClick={() => void commit()} disabled={submitting}>
                  {submitting ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// First-line preview: everything up to the first newline, trimmed. Preserves
// whitespace-sensitive notes (the textarea keeps them intact) but collapses
// multi-line content to a single readable row in the grid.
function firstLine(text: string): string {
  const nl = text.indexOf('\n');
  return (nl < 0 ? text : text.slice(0, nl)).trim() || text.trim();
}
